import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  collection,
  doc,
  serverTimestamp,
  setDoc,
  updateDoc,
  Timestamp,
  type DocumentData,
  type QueryDocumentSnapshot,
} from 'firebase/firestore';
import { useAuth } from '@/contexts/AuthContext';
import { db } from '@/lib/firebase';
import type { CreateAnalysisInput, ScientificAnalysis } from '../types/analysis';
import { loadAnalyses, saveAnalyses } from '../mock/analysis';
import {
  cacheKeyFor,
  loadCachedItems,
  removeCachedItem,
  stripUndefined,
  stripUndefinedForUpdate,
  syncCollectionToCache,
  upsertCachedItem,
} from '../utils/firestoreCache';

const COLLECTION = 'analyses';

interface AnalysisContextValue {
  analyses: ScientificAnalysis[];
  getAnalysisById: (id: string) => ScientificAnalysis | undefined;
  createAnalysis: (input: CreateAnalysisInput) => ScientificAnalysis;
  updateAnalysis: (id: string, patch: Partial<CreateAnalysisInput> & { status?: ScientificAnalysis['status'] }) => void;
  deleteAnalysis: (id: string) => void;
}

const AnalysisContext = createContext<AnalysisContextValue | null>(null);

function parseAnalysisDoc(
  snap: QueryDocumentSnapshot<DocumentData>
): { id: string; data: ScientificAnalysis; updatedAt: string; deleted?: boolean } {
  const raw = snap.data();
  const updatedAt =
    raw.updatedAt instanceof Timestamp
      ? raw.updatedAt.toDate().toISOString()
      : new Date().toISOString();

  return {
    id: snap.id,
    updatedAt,
    deleted: Boolean(raw.deleted),
    data: {
      id: snap.id,
      title: raw.title,
      types: raw.types ?? [],
      customTypeLabel: raw.customTypeLabel,
      researchObjectives: raw.researchObjectives ?? [],
      eventIds: raw.eventIds ?? [],
      createdBy: raw.createdBy,
      collaboratorEmails: raw.collaboratorEmails ?? [],
      createdAt: raw.createdAt ?? updatedAt,
      status: raw.status ?? 'rascunho',
    },
  };
}

export function AnalysisProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const email = user?.email ?? '';
  const useFirestore = Boolean(db && user);
  const cacheKey = user ? cacheKeyFor(COLLECTION, user.uid) : '';

  const [analyses, setAnalyses] = useState<ScientificAnalysis[]>(() =>
    useFirestore ? loadCachedItems<ScientificAnalysis>(cacheKey) : loadAnalyses()
  );

  useEffect(() => {
    if (!useFirestore || !db || !user) return;
    setAnalyses(loadCachedItems<ScientificAnalysis>(cacheKey));
    syncCollectionToCache<ScientificAnalysis>({
      db,
      cacheKey,
      collectionName: COLLECTION,
      parseDoc: parseAnalysisDoc,
    })
      .then(setAnalyses)
      .catch((err) => console.error('Falha ao sincronizar análises com Firestore', err));
  }, [useFirestore, cacheKey, user]);

  const persistMock = useCallback((next: ScientificAnalysis[]) => {
    setAnalyses(next);
    saveAnalyses(next);
  }, []);

  const getAnalysisById = useCallback(
    (id: string) => analyses.find((a) => a.id === id),
    [analyses]
  );

  const createAnalysis = useCallback(
    (input: CreateAnalysisInput): ScientificAnalysis => {
      const ownerEmail = email || 'pesquisador@conectar.app';
      const base: ScientificAnalysis = {
        id: '',
        title: input.title,
        types: input.types,
        customTypeLabel: input.customTypeLabel,
        researchObjectives: input.researchObjectives,
        eventIds: input.eventIds,
        createdBy: ownerEmail,
        collaboratorEmails: input.collaboratorEmails.length
          ? input.collaboratorEmails
          : [ownerEmail],
        createdAt: new Date().toISOString(),
        status: 'rascunho',
      };

      if (useFirestore && db) {
        const ref = doc(collection(db, COLLECTION));
        const item: ScientificAnalysis = { ...base, id: ref.id };
        setAnalyses((prev) => [item, ...prev]);
        upsertCachedItem(cacheKey, item.id, item);
        setDoc(ref, stripUndefined({ ...item, updatedAt: serverTimestamp() })).catch((err) =>
          console.error('Falha ao criar análise no Firestore', err)
        );
        return item;
      }

      const item: ScientificAnalysis = { ...base, id: `an-${Date.now()}` };
      persistMock([item, ...analyses]);
      return item;
    },
    [analyses, email, persistMock, useFirestore, cacheKey]
  );

  const updateAnalysis = useCallback(
    (id: string, patch: Partial<CreateAnalysisInput> & { status?: ScientificAnalysis['status'] }) => {
      const current = analyses.find((a) => a.id === id);
      if (!current) return;
      const updated: ScientificAnalysis = { ...current, ...patch };

      setAnalyses((prev) => prev.map((a) => (a.id === id ? updated : a)));

      if (useFirestore && db) {
        upsertCachedItem(cacheKey, id, updated);
        updateDoc(
          doc(db, COLLECTION, id),
          stripUndefinedForUpdate({ ...patch, updatedAt: serverTimestamp() })
        ).catch((err) => console.error('Falha ao atualizar análise no Firestore', err));
        return;
      }

      persistMock(analyses.map((a) => (a.id === id ? updated : a)));
    },
    [analyses, persistMock, useFirestore, cacheKey]
  );

  const deleteAnalysis = useCallback(
    (id: string) => {
      setAnalyses((prev) => prev.filter((a) => a.id !== id));

      if (useFirestore && db) {
        removeCachedItem(cacheKey, id);
        updateDoc(doc(db, COLLECTION, id), {
          deleted: true,
          updatedAt: serverTimestamp(),
        }).catch((err) => console.error('Falha ao excluir análise no Firestore', err));
        return;
      }

      persistMock(analyses.filter((a) => a.id !== id));
    },
    [analyses, persistMock, useFirestore, cacheKey]
  );

  const value = useMemo(
    () => ({ analyses, getAnalysisById, createAnalysis, updateAnalysis, deleteAnalysis }),
    [analyses, getAnalysisById, createAnalysis, updateAnalysis, deleteAnalysis]
  );

  return (
    <AnalysisContext.Provider value={value}>{children}</AnalysisContext.Provider>
  );
}

export function useAnalysis() {
  const ctx = useContext(AnalysisContext);
  if (!ctx) {
    throw new Error('useAnalysis deve ser usado dentro de AnalysisProvider');
  }
  return ctx;
}
